import React, { useEffect, useRef, useState } from 'react';
import { darken } from 'polished';
import history from 'lib/history';
import routes from 'router/routes';
import { paretoLegend, paretoChart } from 'pages/pareto/test-ids';
import { TParetoDefaultViewProps } from 'pages/pareto/default-view';
import { renderNoDataGraph } from 'components/visuals/multi-line-graph/no-data-graph';
import { mousemove, drawTooltip, updateTooltipData } from 'pages/pareto/default-view/d3-elements';
import colors from 'theme/colors';
import { formatNumber } from 'lib/helpers/currency';
import { IParetoProductWithDetails } from 'store/pareto/types';
import { IStatusFieldOption } from 'store/field-options/types';
import { EDisplay } from 'components/deal-results/product-items';
import { measureMaxTextWidth } from 'lib/helpers/visuals';
import font from 'theme/visuals';
import { axisBottom, axisLeft, scaleLinear, ScaleLinear, select, selectAll, Selection } from 'd3';
import {
  addBtnDarkenValue,
  addBtnHeightAttrValue,
  addBtnWidthAttrValue,
  availableStatusesReduceValue,
  bottomRightLegendTopPosValOne,
  bottomRightLegendTopPosValTwo,
  cancelBtnTextXValue,
  cancelBtnTextYParetoValue,
  cancelBtnYParetoValue,
  captionTextYAttrValue,
  divideByTwoValue,
  dividerHeightValue,
  goToDealBtnTextXValue,
  goToDealBtnTextYParetoValue,
  goToDealBtnYParetoValue,
  lblXPosValue,
  lblYPosValue,
  leftAlignValue,
  legendCircleRadiusValue,
  legendMaxTextWidthCompareValue,
  legendTextXAttrValue,
  legendTextXSpaceValue,
  legendTextYAttrValue,
  measureMaxTextWidthValue,
  oneEmFontSize,
  removeFromChartBtnTextXValue,
  removeFromChartBtnTextYParetoValue,
  removeFromChartBtnYParetoValue,
  selectedDealOpacityValue,
  semiBoldFontWeight,
  sqrtOfValue,
  svgBarHeightAttrValue,
  textBaseYValue,
  tooltipDarkenValue,
  tooltipDateRangeYValue,
  tooltipInnerHalfOffsetValue,
  tooltipNameYValue,
  tooltipRXAttrValue,
  tooltipRYAttrValue,
  tooltipRegionProductLineYValue,
  tooltipTranslateXValue,
  tooltipTranslateYValue,
  twentyFiveHundredthsValue,
  windowHeightOffsetValue,
  xAxisLabelPrefixK,
  xAxisLabelPrefixM,
  xAxisLabelPrefixMM,
  xAxisTickAgrsValue,
  xAxisTickPaddingValue,
  yAxisTickAgrsValue,
  yAxisTickPaddingValue,
} from 'constants/numericalValues';

const fontFamily = 'font-family';
const dataTestId = 'data-testid';
const VOLUME_UNIT_MIN_VALUE = 5000;
const VOLUME_UNIT_MAX_VALUE = 5000000;

// Allow the legend position to move so user can select a position were it does not overlap with other labels.
function makeLegendPositions(width: number, height: number) {
  return {
    topRight: {
      position: { topPos: -10, horizontalPos: width, leftAlign: false },
      nextPos: 'bottomRight',
    },
    bottomRight: {
      position: {
        topPos: height + bottomRightLegendTopPosValOne - bottomRightLegendTopPosValTwo,
        horizontalPos: width,
        leftAlign: false,
      },
      nextPos: 'topLeft',
    },
    topLeft: {
      position: { topPos: -10, horizontalPos: 20, leftAlign: true },
      nextPos: 'topRight',
    },
  };
}

function addLegend(
  svgParent: Selection<SVGGElement, unknown, null, undefined>,
  availableStatuses: IStatusFieldOption[],
  position: { topPos: number; horizontalPos: number; leftAlign?: boolean }
): Selection<SVGGElement, unknown, null, undefined> {
  const { topPos, horizontalPos, leftAlign = true } = position;
  const leftOffset = leftAlign
    ? 0
    : availableStatuses.reduce(
        (acc, current) =>
          acc + measureMaxTextWidth([current.name], measureMaxTextWidthValue) + availableStatusesReduceValue,
        0
      ) - leftAlignValue;

  const legend = svgParent.append('g').attr('transform', `translate(${horizontalPos - leftOffset},${topPos})`);

  let statusLegendCurrentX = 0;
  let otherStatusIndex = -1;

  // Only show statuses that exist in the chart
  availableStatuses.forEach((status, index) => {
    if (status.name === 'other') {
      otherStatusIndex = index;
      return;
    }

    legend
      .append('circle')
      .attr('r', legendCircleRadiusValue)
      .attr('cx', statusLegendCurrentX)
      .attr('cy', 0)
      .attr('fill', status.color);
    legend
      .append('text')
      .text(status.displayName)
      .attr('x', statusLegendCurrentX + legendTextXAttrValue)
      .attr('y', legendTextYAttrValue);

    statusLegendCurrentX +=
      measureMaxTextWidth([status.displayName], legendMaxTextWidthCompareValue) + legendTextXSpaceValue;
  });

  if (otherStatusIndex > -1) {
    legend
      .append('circle')
      .attr('r', legendCircleRadiusValue)
      .attr('cx', statusLegendCurrentX)
      .attr('cy', 0)
      .attr('fill', availableStatuses[otherStatusIndex].color);
    legend
      .append('text')
      .text(availableStatuses[otherStatusIndex].displayName)
      .attr('x', statusLegendCurrentX + legendTextXAttrValue)
      .attr('y', legendTextYAttrValue);
  }

  return legend;
}

interface BarData {
  label: string;
  value: number;
  volume: number;
  cumVolume: number;
  productDetails: IParetoProductWithDetails;
}

function collectData(
  paretoDealProducts: IParetoProductWithDetails[],
  selectedDisplay: EDisplay,
  hasVolumeWidthBars: boolean
): BarData[] {
  let cumVolume = 0;
  const labelsData: BarData[] = [];
  paretoDealProducts.forEach(pdp => {
    const volume = hasVolumeWidthBars ? pdp.totalVolume ?? 0 : 1;
    cumVolume += volume;
    labelsData.push({
      label: `${pdp.customer} \u2014 ${pdp.dealName}`,
      value: pdp[selectedDisplay] ?? 0,
      volume,
      cumVolume,
      productDetails: pdp,
    });
  });

  return labelsData;
}

function addBarLabels(
  svgParent: Selection<SVGGElement, unknown, null, undefined>,
  x: ScaleLinear<number, number>,
  y: ScaleLinear<number, number>,
  barData: BarData[]
) {
  const minLabelWidth = 10; // width in px needed to display a label
  if (barData.length === 0) {
    return;
  }

  const lblGroup = svgParent.append('g');

  let prevXPos = Infinity;
  let prevYPos = -Infinity;
  for (let i = barData.length - 1; i >= 0; i--) {
    const lblData = barData[i];
    const xPos = x(lblData.cumVolume - lblData.volume / divideByTwoValue) + lblXPosValue;
    const yPos = y(Math.max(0, lblData.value)) - lblYPosValue;

    if (prevXPos - xPos > minLabelWidth || prevYPos - yPos > minLabelWidth) {
      lblGroup
        .append('g')
        .attr('transform', `translate(${xPos}, ${yPos})`)
        .append('text')
        .text(lblData.label)
        .attr('transform', `rotate(-45)`);
    }

    prevXPos = xPos;
    prevYPos = yPos;
  }
}

function calcMaxYWithLabels(pxHeight: number, fontSize: number, minValue: number, barData: BarData[]): number {
  // Calculate space for needed for label on top of bars and then rescale the axis to fit this.

  const minHeights = barData.map(bar => {
    // Divide length in pixels by square root of 2 because the label is at a 45 degree angle
    const labelHeight = measureMaxTextWidth([bar.label], fontSize) / Math.sqrt(sqrtOfValue) + fontSize;
    // Always reserve some room for the bars (in part to avoid numerical problems)
    const minimumPortionForBars = 0.5;
    const pxsForBar = Math.max(minimumPortionForBars * pxHeight, pxHeight - labelHeight);
    // For negative values the top of the bar is at 0 rather than lv.value
    const valueRange = Math.max(0, bar.value) - minValue;

    return minValue + (pxHeight / pxsForBar) * valueRange;
  });

  return Math.max(...minHeights);
}

function calcMaxXWithLables(pxWidth: number, fontSize: number, barData: BarData[]) {
  // Calculate space needed for label on the last Bar
  const minWidths = barData.map(bar => {
    // Divide length in pixels by square root of 2 because the label is at a 45 degree angle
    const lastLabelWidth = measureMaxTextWidth([bar.label], fontSize) / Math.sqrt(sqrtOfValue) + fontSize;

    const minimumPortionForBars = 0.8;
    const dataPixels = Math.max(minimumPortionForBars * pxWidth, pxWidth - lastLabelWidth);

    return (pxWidth / dataPixels) * bar.cumVolume;
  });

  return Math.max(...minWidths);
}

function setAxesHelper(maxY: number, minY: number) {
  if (minY < 0) {
    return 0;
  }
  return twentyFiveHundredthsValue * (maxY - minY);
}

function setAxes(
  _svgParent: Selection<SVGGElement, unknown, null, undefined>,
  startYAxisAtZero: boolean,
  pxWidth: number,
  pxHeight: number,
  labelsData: BarData[]
): { x: ScaleLinear<number, number>; y: ScaleLinear<number, number> } {
  const x = scaleLinear().range([0, pxWidth]);
  const y = scaleLinear().range([pxHeight, 0]);

  if (!labelsData || labelsData.length === 0) {
    return { x, y };
  }

  x.domain([0, calcMaxXWithLables(pxWidth, font.size, labelsData)]);

  // Pareto chart is sorted in descending order
  const minY = labelsData && labelsData[labelsData.length - 1].value;
  const maxY = labelsData?.[0].value;

  // Only allow start at zero when there are no negative values, otherwise users
  // get confused because they don't see the negative bars.
  // If the bars are positive, we want to show a little bit of the lowest bar.
  // If the bars are negative, the graph can just start at the bottom of the lowest bar.
  const minValue = startYAxisAtZero && minY > 0 ? 0 : minY - setAxesHelper(maxY, minY);
  const maxValue = calcMaxYWithLabels(pxHeight, font.size, minValue, labelsData);

  y.domain([minValue, maxValue]);

  return { x, y };
}
const checkVolumeUnitLabelForT = (volumeUnitLabel: string, maxX: number) => {
  return volumeUnitLabel === 'T' && maxX > VOLUME_UNIT_MIN_VALUE;
};

const checkVolumeUnitLabelForLB = (volumeUnitLabel: string, maxX: number) => {
  return volumeUnitLabel === 'LB' && maxX > VOLUME_UNIT_MIN_VALUE;
};

const checkVolumeUnitLabelForLBAndMaxXValue = (volumeUnitLabel: string, maxX: number) => {
  return volumeUnitLabel === 'LB' && maxX > VOLUME_UNIT_MAX_VALUE;
};

function drawAxes(
  svgParent: Selection<SVGGElement, unknown, null, undefined>,
  x: ScaleLinear<number, number>,
  y: ScaleLinear<number, number>,
  hasVolumeWidthBars: boolean,
  pricePerUnitLabel: string,
  volumeUnitLabel: string
) {
  const minY = y.domain()[0];
  const maxX = x.domain()[1];

  const xAxisLabelHelper = (d: string | number, scale: number) => {
    return typeof d === 'number' ? formatNumber(scale * d, 0) : d;
  };

  const xAxisLabel = (d: number): string => {
    let scale = 1;
    let prefix = '';
    if (checkVolumeUnitLabelForT(volumeUnitLabel, maxX)) {
      scale = xAxisLabelPrefixK;
      prefix = 'K';
    }

    if (checkVolumeUnitLabelForLB(volumeUnitLabel, maxX)) {
      scale = xAxisLabelPrefixM;
      prefix = 'M';
    }

    if (checkVolumeUnitLabelForLBAndMaxXValue(volumeUnitLabel, maxX)) {
      scale = xAxisLabelPrefixMM;
      prefix = 'MM';
    }

    return d === 0 && minY < 0 ? '' : `${xAxisLabelHelper(d, scale)} ${prefix}${volumeUnitLabel}`;
  };

  // X-axis
  svgParent
    .append('g')
    .attr('transform', `translate(0, ${y(Math.max(0, minY))})`)
    .style('font-size', font.size)
    .style(fontFamily, font.family)
    .call(
      hasVolumeWidthBars
        ? axisBottom(x)
            .tickPadding(xAxisTickPaddingValue)
            .tickArguments([xAxisTickAgrsValue])
            // @ts-ignore
            .tickFormat(d => xAxisLabel(d))
        : axisBottom(x).tickArguments([0])
    );

  // Y-axis
  svgParent
    .append('g')
    .style(fontFamily, font.family)
    .style('font-size', font.size)
    .style(fontFamily, font.family)
    .attr('class', 'pareto-y-axis')
    .call(
      axisLeft(y)
        .tickPadding(yAxisTickPaddingValue)
        .tickArguments([yAxisTickAgrsValue])
        .tickFormat(d => `${typeof d === 'number' ? formatNumber(d, 0) : d.valueOf} ${pricePerUnitLabel}`)
    );
}

const checkSelectedDisplayData = (selectedDisplayData: number, barSpacing: number) => {
  return selectedDisplayData < 0 ? 0 : -barSpacing;
};

const checkHasVolumeWidthBars = (hasVolumeWidthBars: boolean, pdp: IParetoProductWithDetails) => {
  return hasVolumeWidthBars ? pdp.totalVolume ?? 0 : 1;
};

const getSelectedDisplayData = (selectedDisplayData: number) => {
  return selectedDisplayData || 0;
};

const handleClickBehaviourHelper = (selectedProductName: string, selectedDealName: string) => {
  selectAll(`.pdp-group:not([data-dealname="${selectedDealName}"])`).style('opacity', selectedDealOpacityValue);
  selectAll(`.product-tooltip[data-dealname="${selectedDealName}"][data-productname="${selectedProductName}"]`)
    .style('display', null)
    .raise();
};

const ParetoDefaultView = ({
  availableStatuses,
  legendText,
  paretoDealProducts,
  pricePerUnitLabel,
  removeParetoDealAndId,
  selectedDisplay,
  selectedUnitType,
  volumeUnitLabel,
  hasVolumeWidthBars,
}: TParetoDefaultViewProps) => {
  const [selectedDealName, setSelectedDealName] = useState('');
  const [selectedProductName, setSelectedProductName] = useState('');
  const [startYAxisAtZero, setStartYAxisAtZero] = useState(false);
  const [legendPosition, setLegendPosition] = useState('topRight');

  const paretoRef = useRef<HTMLDivElement>(null);
  const uniqueParetoClassName = 'pareto-visual';

  // Handle click behavior (show tooltips and make other products slightly transparent)
  useEffect(() => {
    selectAll('.pdp-group').style('opacity', 1);
    selectAll('.product-tooltip').style('display', 'none');
    if (selectedProductName && selectedDealName) {
      handleClickBehaviourHelper(selectedProductName, selectedDealName);
    }
  }, [selectedDealName, selectedProductName]);

  useEffect(() => {
    const dimensions = { width: 900, height: 600 };
    const margin = {
      top: 64,
      right: 32,
      bottom: 64,
      left: 156,
    };

    // Constants
    const aspectRatio = 0.667;
    const barSpacing = 2;

    // Calculate dimensions
    if (paretoRef?.current) {
      dimensions.width = paretoRef.current.offsetWidth;
      dimensions.height = paretoRef.current.offsetWidth * aspectRatio;
    }

    // Ensure that height does not bleed off screen
    const windowHeight = window.innerHeight;
    const maxWindowHeight = windowHeight - windowHeightOffsetValue;
    if (dimensions.height > maxWindowHeight) {
      dimensions.height = maxWindowHeight;
    }

    // Ensure that height is large enough to display tooltips (670px)
    const minWindowHeight = 670;
    if (dimensions.height < minWindowHeight) {
      dimensions.height = minWindowHeight;
    }

    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    // Clear old SVG
    selectAll(`.${uniqueParetoClassName} > *`).remove();

    // Create container
    const svg = select(paretoRef.current)
      .append('svg')
      .attr(fontFamily, font.family)
      .attr('font-size', font.size)
      .attr('width', width + margin.right + margin.left)
      .attr('height', height + margin.bottom + margin.top)
      .attr(dataTestId, paretoChart)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const graphData = collectData(paretoDealProducts, selectedDisplay, hasVolumeWidthBars);

    // Set the ranges
    const { x, y } = setAxes(svg, startYAxisAtZero, width, height, graphData);

    // Handle no data
    if (!paretoDealProducts.length) {
      renderNoDataGraph({ svg, x, y, width, height });
      return;
    }

    // Draw caption text
    svg
      .append('text')
      .attr('x', 0)
      .attr('y', height + margin.bottom - captionTextYAttrValue)
      .attr(dataTestId, paretoLegend)
      .text(legendText);

    // Tooltip
    drawTooltip(svg);

    addBarLabels(svg, x, y, graphData);

    // Draw boxes
    graphData.forEach(bar => {
      const selectedDisplayData = bar.productDetails[selectedDisplay];
      if (!selectedDisplayData) {
        return;
      }

      const pdpGroup = svg.append('g').attr('transform', `translate(${x(bar.cumVolume - bar.volume)},0)`);

      // Positive bars run from y=max(0, startYAxis) to y=value. Negative bars run from y=value to y=0
      const startYAxis = y.domain()[0];
      const topOfBar = Math.max(0, selectedDisplayData);
      const bottomOfBar = selectedDisplayData < 0 ? selectedDisplayData : Math.max(0, startYAxis);
      const barHeight = y(bottomOfBar) - y(topOfBar);

      pdpGroup
        .append('rect')
        .attr('class', 'pdp-group')
        .attr('data-dealname', bar.productDetails.dealName)
        .attr('data-productname', bar.productDetails.name)
        .attr('width', x(bar.volume))
        // Make the bar taller to offset the invisile border lines at the top and bottom.
        .attr('height', barHeight + svgBarHeightAttrValue * barSpacing)
        // Heighten the bar to offset the invisible border line on top of the graph
        .attr('y', y(topOfBar) + checkSelectedDisplayData(selectedDisplayData, barSpacing))
        .attr('fill', bar.productDetails.color)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', barSpacing)
        .attr(dataTestId, 'tid-pdp-group')
        .style('cursor', 'pointer')
        .on('mouseover', () => {
          const tt = select('.tooltip-pareto');
          tt.style('display', null);
          tt.raise();
          updateTooltipData(bar.productDetails, selectedUnitType, selectedDisplay);
        })
        .on('mouseout', () => {
          const tt = select('.tooltip-pareto');
          tt.style('display', 'none');
        })
        .on('mousemove', mousemove)
        .on('click', () => {
          setSelectedDealName(bar.productDetails.dealName);
          setSelectedProductName(bar.productDetails.name);
        });
    });

    let currentItemXPosition = barSpacing;
    paretoDealProducts.forEach(pdp => {
      const selectedDisplayData = pdp[selectedDisplay];
      if (!selectedDisplayData) {
        return;
      }

      const barWidth = checkHasVolumeWidthBars(hasVolumeWidthBars, pdp);
      const barHeight = getSelectedDisplayData(selectedDisplayData);

      // Product tooltip
      const productTooltipWidth = 275;
      const productTooltipHeight = 335;
      // All clickable tool tips are immediate children of "svg" so that they can be
      // raised to the top of the entire graph.
      const productTooltip = svg
        .append('g')
        .attr('class', 'product-tooltip')
        .style('display', 'none')
        .attr('data-dealname', pdp.dealName)
        .attr('data-productname', pdp.name);

      const topTooltip = y((barHeight + y.domain()[0]) / divideByTwoValue) - productTooltipHeight;

      productTooltip
        .append('rect')
        .attr('width', productTooltipWidth)
        .attr('height', productTooltipHeight)
        .attr('fill', colors.darkPanelBackground)
        .attr('x', currentItemXPosition + x(barWidth / divideByTwoValue) - productTooltipWidth / divideByTwoValue)
        .attr('y', topTooltip)
        .attr('rx', tooltipRXAttrValue)
        .attr('ry', tooltipRYAttrValue);

      // Close button
      productTooltip
        .append('path')
        .attr(
          'd',
          'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'
        )
        .attr('fill', 'white')
        .attr(
          'transform',
          `translate(${currentItemXPosition +
            x(barWidth / divideByTwoValue) +
            productTooltipWidth / divideByTwoValue -
            tooltipTranslateXValue}, ${topTooltip + tooltipTranslateYValue})`
        )
        .attr(dataTestId, 'tid-product-tooltip-close-button')
        .style('cursor', 'pointer')
        .on('mouseover', function() {
          select(this).style('fill', darken(tooltipDarkenValue, 'white'));
        })
        .on('mouseout', function() {
          select(this).style('fill', 'white');
        })
        .on('click', () => {
          setSelectedDealName('');
          setSelectedProductName('');
        });

      // Constants
      const textVerticalSpacing = 18;
      const tooltipMiddleXPosition = currentItemXPosition + x(barWidth / divideByTwoValue);
      const tooltipInnerHalfWidth = productTooltipWidth / divideByTwoValue - tooltipInnerHalfOffsetValue;
      const textLeftX = tooltipMiddleXPosition - tooltipInnerHalfWidth;

      // Text objects
      const addText = (
        selection: Selection<SVGGElement, unknown, null, undefined>,
        textValue: string,
        yOffset: number
      ) => {
        const textBaseY = topTooltip + textBaseYValue;

        return selection
          .append('text')
          .attr('fill', 'white')
          .attr('x', textLeftX)
          .attr('y', textBaseY + yOffset)
          .text(textValue);
      };
      addText(productTooltip, pdp.customer, 0);
      addText(productTooltip, pdp.dealName, textVerticalSpacing);
      addText(productTooltip, pdp.regionProductLine, tooltipRegionProductLineYValue * textVerticalSpacing);
      addText(productTooltip, pdp.dateRange, tooltipDateRangeYValue * textVerticalSpacing);
      addText(productTooltip, pdp.name, tooltipNameYValue * textVerticalSpacing);

      // Divider
      const dividerHeight = topTooltip + dividerHeightValue;
      productTooltip
        .append('line')
        .attr('x1', tooltipMiddleXPosition - tooltipInnerHalfWidth)
        .attr('y1', dividerHeight)
        .attr('x2', tooltipMiddleXPosition + tooltipInnerHalfWidth)
        .attr('y2', dividerHeight)
        .attr('stroke', 'grey')
        .attr('stroke-width', 1);

      const addButton = (
        selection: Selection<SVGGElement, unknown, null, undefined>,
        yPos: number,
        color = 'transparent'
      ) => {
        return selection
          .append('rect')
          .attr('x', tooltipMiddleXPosition - tooltipInnerHalfWidth)
          .attr('y', yPos)
          .attr('width', addBtnWidthAttrValue)
          .attr('height', addBtnHeightAttrValue)
          .attr('rx', tooltipRXAttrValue)
          .attr('ry', tooltipRYAttrValue)
          .style('cursor', 'pointer')
          .style('stroke', 'grey')
          .attr('fill', color)
          .on('mouseout', function() {
            select(this).style('fill', color);
          });
      };

      const addButtonText = (
        selection: Selection<SVGGElement, unknown, null, undefined>,
        textValue: string,
        xPos: number,
        yPos: number
      ) => {
        return selection
          .append('text')
          .text(textValue)
          .attr('x', xPos)
          .attr('y', yPos)
          .style('pointer-events', 'none')
          .style('font-weight', semiBoldFontWeight)
          .style('font-size', oneEmFontSize);
      };

      // Go to deal button
      addButton(productTooltip, topTooltip + goToDealBtnYParetoValue, colors.yellow)
        .attr(dataTestId, 'tid-product-tooltip-go-to-deal-button')
        .on('mouseover', function() {
          select(this).style('fill', darken(addBtnDarkenValue, colors.yellow));
        })
        .on('click', () => {
          history.push(`${routes.deals}/${pdp.dealId}`);
        });

      addButtonText(
        productTooltip,
        'Go to Deal',
        tooltipMiddleXPosition - productTooltipWidth / divideByTwoValue + goToDealBtnTextXValue,
        topTooltip + goToDealBtnTextYParetoValue
      );

      // Remove from chart button
      addButton(productTooltip, topTooltip + removeFromChartBtnYParetoValue)
        .attr(dataTestId, 'tid-product-tooltip-remove-button')
        .on('mouseover', function() {
          select(this).style('fill', 'rgba(255,255,255,0.1)');
        })
        .on('click', () => {
          setSelectedDealName('');
          setSelectedProductName('');
          removeParetoDealAndId(pdp.dealName, pdp.dealId);
        });

      addButtonText(
        productTooltip,
        'Remove from Chart',
        tooltipMiddleXPosition - productTooltipWidth / divideByTwoValue + removeFromChartBtnTextXValue,
        topTooltip + removeFromChartBtnTextYParetoValue
      ).attr('fill', 'white');

      // Cancel button
      addButton(productTooltip, topTooltip + cancelBtnYParetoValue)
        .attr(dataTestId, 'tid-product-tooltip-cancel-button')
        .on('mouseover', function() {
          select(this).style('fill', 'rgba(255,255,255,0.1)');
        })
        .on('click', () => {
          setSelectedDealName('');
          setSelectedProductName('');
        });

      addButtonText(
        productTooltip,
        'Cancel',
        tooltipMiddleXPosition - productTooltipWidth / divideByTwoValue + cancelBtnTextXValue,
        topTooltip + cancelBtnTextYParetoValue
      ).attr('fill', 'white');

      // Increment x position
      currentItemXPosition = currentItemXPosition + x(barWidth) + barSpacing;
    });

    // Draw axis after data to ensure it is on top of negative bars.
    drawAxes(svg, x, y, hasVolumeWidthBars, pricePerUnitLabel, volumeUnitLabel);

    // Y-Axis can be toggled to start at 0 at slightly below the lowest y value.
    select('.pareto-y-axis')
      .style('cursor', 'pointer')
      .on('click', () => setStartYAxisAtZero(!startYAxisAtZero));

    // Draw legend (other status last)
    const legendPositions = makeLegendPositions(width, height);
    const legend = addLegend(svg, availableStatuses, legendPositions[legendPosition].position);
    legend.attr('cursor', 'pointer').on('click', () => setLegendPosition(legendPositions[legendPosition].nextPos));
  }, [
    legendText,
    selectedDisplay,
    selectedUnitType,
    pricePerUnitLabel,
    uniqueParetoClassName,
    paretoDealProducts,
    volumeUnitLabel,
    availableStatuses,
    removeParetoDealAndId,
    hasVolumeWidthBars,
    startYAxisAtZero,
    legendPosition,
  ]);

  return <div className={uniqueParetoClassName} ref={paretoRef} />;
};

export default ParetoDefaultView;
